const DOT = ".";
const COMA = ",";
const EMPTY = '';
const NEW_LINE = '\n';

const SRT_TIME_SPAN_SEPARATOR = " --> ";
const SRT_TIME_FRACTION_SEPARATOR = ':';
const SRT_ITEM_SEPARATOR = /\n\r?\n/;

const DEFAULT_CHUNK_SIZE = 100;

/**
 * Parsing raw subRip file into no-gap array of subs,
 * wrap real subs with empty head and tail.
 *
 * @param {string} srtString
 * @param {(items: SRTItem[]) => void} onComplete
 * @param {{ chunkSize?: number }?} options - only for testing purpose
 * @return {() => void} cancel fn
 */
export default function parseSRT(srtString, onComplete, options) {
  const chunkSize = options?.chunkSize ?? DEFAULT_CHUNK_SIZE;
  const subsList = createList();

  const srtStringItems = srtString
    .split(SRT_ITEM_SEPARATOR);

  let line = 0;

  let lastTimeoutId = setTimeout(parseChunk);

  return () => {
    clearTimeout(lastTimeoutId);
  };

  function parseChunk() {
    let iteration = 0;
    do {
      const itemString = srtStringItems[line];
      if (itemString) {
        const item = parseSubtitleItem(itemString);
        if (item) {
          subsList.append(item);
        }
      }

      line += 1;
      iteration += 1;
    } while (iteration < chunkSize && line < srtStringItems.length);

    if (line < srtStringItems.length) {
      lastTimeoutId = setTimeout(parseChunk);
    } else {
      onComplete(subsList.finalize());
    }
  }
}

function createList() {
  let tailTime = 0;

  const items = [
    newItem(-Infinity, tailTime, EMPTY)
  ];

  return {
    append,
    finalize() {
      items.push(newItem(tailTime, Infinity, EMPTY));
      return items;
    }
  };

  /**
   *
   * @param {SRTItem} item
   * @return {void}
   */
  function append(item) {
    if (item.startTime > tailTime) {
      items.push(newItem(tailTime, item.startTime, EMPTY));
    }

    items.push(item);
    tailTime = item.endTime;
  }

  function newItem(startTime, endTime, text) {
    return {
      startTime,
      endTime,
      text,
    };
  }
}

/**
 * Format:
 * id
 * hh:mm:ss,ms --> hh:mm:ss,ms
 * text
 *
 * @param {String} str
 * @return {SRTItem}
 */
function parseSubtitleItem(str) {
  /*
     Use manual algorithm, as regexp performs extremely slow on old devices
  */

  const idEndIndex = str.indexOf(NEW_LINE);
  if (idEndIndex === -1) {
    return;
  }

  const timeEndIndex = str.indexOf(NEW_LINE, idEndIndex + 1);
  if (timeEndIndex === -1) {
    return;
  }

  const time = str.substring(idEndIndex + 1, timeEndIndex);
  const timeSpan = time.split(SRT_TIME_SPAN_SEPARATOR);
  if (timeSpan.length !== 2) {
    return;
  }

  const text = str.substring(timeEndIndex + 1);

  return {
    text: text.trim(),
    startTime: parseTime(timeSpan[0]),
    endTime: parseTime(timeSpan[1]),
  };
}

/**
 *
 * @param {string} time
 * @return {number} seconds with ms precision
 */
function parseTime(time) {
  const [ hh, mm, ssMs ] = time.split(SRT_TIME_FRACTION_SEPARATOR);

  // Use Number instead of parseInt / parseFloat as of performance reasons
  return Number(hh) * 3600 +
    Number(mm) * 60 +
    Number(ssMs.replace(COMA, DOT));
}
